import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static defaults = {
    count: 150,
    minSize: 2,
    maxSize: 6,
    minSpeed: 0.5,
    maxSpeed: 2,
    wind: 0.2,
    opacity: 0.8,
    startText: "Detener nieve",
    stopText: "Activar nieve",
  };

  static targets = ["toggleButton"];

  connect() {
    console.log("Snow controller connected!");

    // Desde el 22 de septiembre empieza la primavera
    this.isSpring = this.isSpringTime();

    this.active = true;

    // Crear canvas
    this.canvas = document.createElement("canvas");
    this.canvas.style.position = "fixed";
    this.canvas.style.top = "0";
    this.canvas.style.left = "0";
    this.canvas.style.width = "100vw";
    this.canvas.style.height = "100vh";
    this.canvas.style.pointerEvents = "none";
    this.canvas.style.zIndex = "9999";
    this.canvas.style.backgroundColor = "rgba(255,255,255,0.15)";

    this.element.appendChild(this.canvas);

    this.ctx = this.canvas.getContext("2d");

    this.resizeCanvas();

    // Cargar parámetros
    this.count = this.getNumberAttribute("count");
    this.minSize = this.getNumberAttribute("minSize");
    this.maxSize = this.getNumberAttribute("maxSize");
    this.minSpeed = this.getNumberAttribute("minSpeed");
    this.maxSpeed = this.getNumberAttribute("maxSpeed");
    this.wind = this.getNumberAttribute("wind");
    this.opacity = this.getNumberAttribute("opacity");

    // Inicializar partículas
    this.snowflakes = [];

    for (let i = 0; i < this.count; i++) {
      this.snowflakes.push(this.createSnowflake(true));
    }

    // Iniciar animación
    this.animate = this.animate.bind(this);
    this.frameId = requestAnimationFrame(this.animate);

    this.resizeHandler = this.resizeCanvas.bind(this);
    window.addEventListener("resize", this.resizeHandler);

    this.updateButtonText();

    console.log(
      this.isSpring
        ? "🌸 Primavera activada"
        : "❄️ Invierno / nieve activada"
    );
  }

  disconnect() {
    cancelAnimationFrame(this.frameId);
    window.removeEventListener("resize", this.resizeHandler);

    if (this.canvas) {
      this.canvas.remove();
    }
  }

  toggle() {
    this.active = !this.active;

    if (this.active) {
      this.canvas.style.display = "block";
      this.frameId = requestAnimationFrame(this.animate);
    } else {
      this.canvas.style.display = "none";
      cancelAnimationFrame(this.frameId);
    }

    this.updateButtonText();
  }

  updateButtonText() {
    const button = this.hasToggleButtonTarget
      ? this.toggleButtonTarget
      : null;

    if (!button) return;

    if (this.isSpring) {
      button.textContent = this.active
        ? "Detener primavera"
        : "Activar primavera";
    } else {
      button.textContent = this.active
        ? "Detener nieve"
        : "Activar nieve";
    }
  }

  /*
   * Primavera desde el 22 de septiembre.
   */
  isSpringTime() {
    const now = new Date();

    const month = now.getMonth(); // enero = 0
    const day = now.getDate();

    // 21 de septiembre todavía es invierno.
    // 22 de septiembre en adelante = primavera.
    if (month > 8) return true; // octubre, noviembre, diciembre

    if (month === 8 && day >= 22) return true;

    return false;
  }

  getNumberAttribute(name) {
    const value = this.element.dataset[name];

    if (value !== undefined) {
      const number = parseFloat(value);

      if (!Number.isNaN(number)) {
        return number;
      }
    }

    return this.constructor.defaults[name];
  }

  resizeCanvas() {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  }

  createSnowflake(randomY = false) {
    const x = Math.random() * this.canvas.width;
    const y = randomY
      ? Math.random() * this.canvas.height
      : -this.maxSize;

    const size =
      this.minSize +
      Math.random() * (this.maxSize - this.minSize);

    const speed =
      this.minSpeed +
      Math.random() * (this.maxSpeed - this.minSpeed);

    return {
      x,
      y,
      size,
      speed,

      // Movimiento lateral propio de cada partícula
      drift: (Math.random() - 0.5) * 0.5,

      // Rotación para las flores
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.03,

      // Color de la flor
      color: this.randomFlowerColor(),
    };
  }

  randomFlowerColor() {
    const colors = [
      "#ff6b9d",
      "#ff8fab",
      "#ffd166",
      "#f78c6b",
      "#c77dff",
      "#9be564",
      "#70d6ff",
      "#ff70a6",
    ];

    return colors[Math.floor(Math.random() * colors.length)];
  }

  drawSnowflake(flake) {
    this.ctx.beginPath();

    this.ctx.arc(
      flake.x,
      flake.y,
      flake.size / 2,
      0,
      Math.PI * 2
    );

    this.ctx.fillStyle = `rgba(255, 255, 255, ${this.opacity})`;

    this.ctx.fill();
  }

  drawFlower(flake) {
    const petals = 5;
    const radius = flake.size / 2;

    this.ctx.save();

    this.ctx.translate(flake.x, flake.y);
    this.ctx.rotate(flake.rotation);

    this.ctx.fillStyle = flake.color;

    // Pétalos
    for (let i = 0; i < petals; i++) {
      const angle = (Math.PI * 2 * i) / petals;

      const x = Math.cos(angle) * radius * 0.7;
      const y = Math.sin(angle) * radius * 0.7;

      this.ctx.beginPath();

      this.ctx.ellipse(
        x,
        y,
        radius * 0.55,
        radius * 0.3,
        angle,
        0,
        Math.PI * 2
      );

      this.ctx.fill();
    }

    // Centro
    this.ctx.beginPath();
    this.ctx.arc(
      0,
      0,
      radius * 0.25,
      0,
      Math.PI * 2
    );

    this.ctx.fillStyle = "#ffd166";
    this.ctx.fill();

    this.ctx.restore();
  }

  animate() {
    if (!this.active) return;

    this.ctx.clearRect(
      0,
      0,
      this.canvas.width,
      this.canvas.height
    );

    for (const flake of this.snowflakes) {
      if (this.isSpring) {
        this.drawFlower(flake);
      } else {
        this.drawSnowflake(flake);
      }

      // Movimiento vertical
      flake.y += flake.speed;

      // Movimiento horizontal
      flake.x +=
        this.wind * (flake.size / this.maxSize) +
        flake.drift;

      // Rotación de las flores
      if (this.isSpring) {
        flake.rotation += flake.rotationSpeed;
      }

      // Volvió a salir por abajo
      if (flake.y > this.canvas.height + flake.size) {
        Object.assign(flake, this.createSnowflake(false));

        flake.x = Math.random() * this.canvas.width;
      }

      // Salió por los laterales
      if (
        flake.x < -flake.size ||
        flake.x > this.canvas.width + flake.size
      ) {
        flake.x = Math.random() * this.canvas.width;
        flake.y = -flake.size;
      }
    }

    this.frameId = requestAnimationFrame(this.animate);
  }
}
